import React from "react";
import { Modal, Button } from "react-bootstrap";
import styled from "@emotion/styled";
import {
    PrivacyPolicyContent,
    TermsOfServiceContent,
} from "constant/legalDocuments";

const StyledModal = styled(Modal)`
    & .modal-dialog {
        max-width: 80%;
    }

    & .modal-content {
        max-height: 85vh;
        display: flex;
        flex-direction: column;
    }

    & .modal-header {
        background-color: #1c3e52;
        border-bottom: none;
        flex-shrink: 0;
        padding: 1rem 1.5rem;

        .modal-title {
            color: #ffffff;
            font-size: 1.125rem;
            font-weight: 500;
        }

        .close-button {
            color: #ffffff;
            opacity: 1;
            padding: 0;
            margin: 0;

            &:hover {
                color: #ffffff;
                opacity: 0.8;
            }

            .material-icons {
                font-size: 1.5rem;
            }
        }
    }

    & .modal-body {
        overflow-y: auto;
        flex: 1;
        padding: 1.5rem;
    }
`;

const DocumentContent = styled.div`
    font-size: 14px;
    line-height: 1.6;
    color: #333333;

    h1 {
        font-size: 1.5rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: #1a1a1a;
    }

    h2 {
        font-size: 1.25rem;
        font-weight: 600;
        margin-top: 1.5rem;
        margin-bottom: 0.75rem;
        color: #2a2a2a;
    }

    h3 {
        font-size: 1.1rem;
        font-weight: 600;
        margin-top: 1rem;
        margin-bottom: 0.5rem;
        color: #333333;
    }

    p {
        margin-bottom: 0.75rem;
        color: #333333;
    }

    ul,
    ol {
        margin-bottom: 0.75rem;
        padding-left: 1.5rem;
        list-style-type: disc;
    }

    ol {
        list-style-type: decimal;
    }

    li {
        margin-bottom: 0.25rem;
        color: #333333;
    }

    a {
        color: #00bfa5;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    hr {
        margin: 2rem 0;
        border-top: 2px solid #dee2e6;
    }
`;

export interface LegalDocumentsModalProps {
    show: boolean;
    onHide: () => void;
    documentType: "privacy" | "terms";
}

const LegalDocumentsModal: React.FC<LegalDocumentsModalProps> = ({
    show,
    onHide,
    documentType,
}) => {
    const isPrivacyModal = documentType === "privacy";

    return (
        <StyledModal
            show={show}
            onHide={onHide}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
        >
            <Modal.Header>
                <Modal.Title data-testid="legal-documents-modal-title">
                    {isPrivacyModal ? "Privacy Policy" : "Terms of Service"}
                </Modal.Title>
                <Button
                    variant=""
                    className="close-button"
                    onClick={onHide}
                    aria-label="Close"
                >
                    <span className="material-icons">close</span>
                </Button>
            </Modal.Header>
            <Modal.Body>
                <DocumentContent>
                    {isPrivacyModal ? (
                        <PrivacyPolicyContent />
                    ) : (
                        <TermsOfServiceContent />
                    )}
                </DocumentContent>
            </Modal.Body>
        </StyledModal>
    );
};

export default LegalDocumentsModal;
